import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

const ticketData = [
  { name: 'Mon', open: 24, resolved: 35, in_progress: 20 },
  { name: 'Tue', open: 28, resolved: 38, in_progress: 25 },
  { name: 'Wed', open: 20, resolved: 42, in_progress: 18 },
  { name: 'Thu', open: 32, resolved: 39, in_progress: 30 },
  { name: 'Fri', open: 25, resolved: 45, in_progress: 22 },
  { name: 'Sat', open: 15, resolved: 28, in_progress: 12 },
  { name: 'Sun', open: 18, resolved: 32, in_progress: 14 },
]

const aiPerformanceData = [
  { name: 'Mon', resolution: 75, escalation: 25 },
  { name: 'Tue', resolution: 78, escalation: 22 },
  { name: 'Wed', resolution: 82, escalation: 18 },
  { name: 'Thu', resolution: 79, escalation: 21 },
  { name: 'Fri', resolution: 85, escalation: 15 },
  { name: 'Sat', resolution: 80, escalation: 20 },
  { name: 'Sun', resolution: 76, escalation: 24 },
]

const channelData = [
  { name: 'Chat', value: 45, color: '#0284c7' },
  { name: 'Email', value: 25, color: '#10b981' },
  { name: 'SMS', value: 20, color: '#f59e0b' },
  { name: 'Phone', value: 10, color: '#ef4444' },
]

const trainingData = [
  { name: 'Week 1', enrolled: 120, completed: 95 },
  { name: 'Week 2', enrolled: 145, completed: 110 },
  { name: 'Week 3', enrolled: 160, completed: 135 },
  { name: 'Week 4', enrolled: 175, completed: 155 },
]

export default function AnalyticsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Analytics</h1>
        <p className="text-blue-200 mt-1">Monitor performance and key metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Tickets', value: '1,234', change: '+12%' },
          { label: 'AI Resolution Rate', value: '81.5%', change: '+2.3%' },
          { label: 'Avg Response Time', value: '2h 14m', change: '-15%' },
          { label: 'Customer Satisfaction', value: '4.7/5', change: '+0.2' },
        ].map((metric, idx) => (
          <div key={idx} className="card">
            <div className="card-body">
              <p className="text-blue-200 text-sm">{metric.label}</p>
              <p className="text-2xl font-bold text-white mt-2">{metric.value}</p>
              <p className="text-green-600 text-xs font-medium mt-2">{metric.change}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Trends */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">Ticket Trends</h3>
          </div>
          <div className="card-body h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ticketData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="open" fill="#ef4444" />
                <Bar dataKey="in_progress" fill="#f59e0b" />
                <Bar dataKey="resolved" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Performance */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">AI Performance</h3>
          </div>
          <div className="card-body h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={aiPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="resolution" stroke="#10b981" strokeWidth={2} name="Resolution Rate %" />
                <Line type="monotone" dataKey="escalation" stroke="#ef4444" strokeWidth={2} name="Escalation %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Channel Distribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">Tickets by Channel</h3>
          </div>
          <div className="card-body h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Training Completion */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">Training Enrollment & Completion</h3>
          </div>
          <div className="card-body h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trainingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="enrolled" stroke="#0284c7" strokeWidth={2} />
                <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-white">Top Issues</h3>
        </div>
        <div className="card-body">
          <div className="space-y-3">
            {[
              { issue: 'Password Reset', count: 245, change: '+5%' },
              { issue: 'Billing Inquiry', count: 187, change: '+2%' },
              { issue: 'Course Access', count: 156, change: '-1%' },
              { issue: 'Technical Issue', count: 134, change: '+8%' },
              { issue: 'Upgrade Request', count: 98, change: '-3%' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border-b border-blue-500/30 last:border-b-0">
                <span className="font-medium text-white">{item.issue}</span>
                <div className="flex items-center space-x-4">
                  <span className="text-blue-200">{item.count} tickets</span>
                  <span className={`text-sm font-medium ${item.change.startsWith('+') ? 'text-red-600' : 'text-green-600'}`}>
                    {item.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
